import React from 'react';

/**
 * Canonical page header for the admin & customer dashboards.
 *
 * Phase-1 of the design refresh: replaced the loud cyan→blue gradient
 * icon-tile with a quiet outlined chip so the page title leads instead
 * of competing with a bright square. The icon chip still uses brand
 * accent colour but in a calm 1-px outline + tinted background.
 *
 *   <PageHeader
 *     icon={<MessageSquare className="w-4 h-4" />}
 *     title="Tickets"
 *     subtitle="All conversations across all orders"
 *     meta="Auto-refreshes every 20s"
 *     actions={<button>Export</button>}
 *   />
 */
export default function PageHeader({ icon, title, subtitle, meta, actions, testid, tone = 'cyan' }) {
  const iconColor = {
    cyan:    'bg-cyan-50    text-cyan-600    border-cyan-200',
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    amber:   'bg-amber-50   text-amber-600   border-amber-200',
    rose:    'bg-rose-50    text-rose-600    border-rose-200',
    slate:   'bg-slate-100  text-slate-600   border-slate-200',
  }[tone] || 'bg-cyan-50 text-cyan-600 border-cyan-200';

  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-5"
      data-testid={testid || 'page-header'}
    >
      <div className="flex items-start gap-3 min-w-0">
        {icon && (
          <div className={`w-9 h-9 rounded-lg border flex items-center justify-center flex-shrink-0 ${iconColor}`}>
            {icon}
          </div>
        )}
        <div className="min-w-0">
          <h1 className="text-[20px] sm:text-[22px] font-semibold text-slate-900 leading-tight truncate tracking-tight">
            {title}
          </h1>
          {subtitle && <p className="text-[13px] text-slate-500 leading-snug mt-0.5">{subtitle}</p>}
          {meta && <p className="text-[11px] text-slate-400 mt-1 leading-tight">{meta}</p>}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
    </div>
  );
}

/**
 * Small section header used between groups of cards/tables — e.g. on
 * Dashboard between "Alerts", "Main KPIs", "Today" etc. Keep tight: just
 * a label + optional helper text. No background, no border.
 */
export function SectionLabel({ children, hint, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-3 mb-2 mt-4 first:mt-0" data-testid={testid}>
      <h2 className="text-[11px] font-bold text-slate-500 uppercase tracking-[0.08em]">{children}</h2>
      {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
    </div>
  );
}
